import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import apiClient from '../../api/client';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Queue as QueueIcon,
  People as PeopleIcon,
  Timer as TimerIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Notifications as NotificationsIcon,
  Assessment as AssessmentIcon,
  Speed as SpeedIcon,
  AccessTime as AccessTimeIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

// Типы для данных дашборда
interface MetricCard {
  title: string;
  value: string | number;
  changeText?: string;
  trend?: 'up' | 'down' | 'neutral';
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
}

interface QueueStatus {
  id: string;
  name: string;
  status: string;
  code?: string | null;
  waitingCount?: number;
  completedToday?: number;
  avgServiceTimeMinutes?: number;
  efficiency?: number;
  totalToday?: number;
  activeOperators?: number;
}

interface OperatorStatus {
  id: string;
  name: string;
  status: 'available' | 'busy' | 'break' | 'offline';
  ticketsToday: number;
  avgServiceTime: number;
  windowNumber?: number;
  currentTicket?: string;
}

interface SystemAlert {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  message: string;
  timestamp: Date;
  acknowledged: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const DashboardPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [controlDialog, setControlDialog] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<SystemAlert[]>([]);

  // Загрузка основных данных
  const { data: health, refetch: refetchHealth } = useQuery(['health'], async () => {
    const res = await apiClient.get('/health');
    return res.data;
  }, { staleTime: 30_000, refetchInterval: 30_000 });

  const { data: analyticsData, refetch: refetchAnalytics } = useQuery(['analytics'], async () => {
    const res = await apiClient.get('/api/analytics/overview');
    return res.data;
  }, { staleTime: 60_000, refetchInterval: 60_000 });

  const { data: operatorsData, refetch: refetchOperators } = useQuery(['operators'], async () => {
    const res = await apiClient.get('/api/operators');
    return res.data;
  }, { staleTime: 15_000, refetchInterval: 20_000 });

  const { data: usersData } = useQuery(['users'], async () => {
    const res = await apiClient.get('/api/admin/users');
    return res.data;
  }, { staleTime: 60_000 });

  // Обновление всех данных
  const refreshAll = () => {
    refetchHealth();
    refetchAnalytics();
    refetchOperators();
  };

  // Автоматические уведомления на основе фактических метрик
  const analyticsOverview = analyticsData?.data;
  const totalOperators = operatorsData?.data?.operators?.length || 0;

  useEffect(() => {
    if (!analyticsOverview) {
      setAlerts([]);
      return;
    }

    setAlerts((previousAlerts) => {
      const acknowledgedMap = new Map<string, boolean>();
      previousAlerts.forEach((alert) => {
        if (alert.acknowledged) {
          acknowledgedMap.set(alert.id, true);
        }
      });

      const now = new Date();
      const generatedAlerts: SystemAlert[] = [];
      const queuePerformance = (analyticsOverview.queuePerformance || []) as QueueStatus[];
      const totalWaiting =
        analyticsOverview.totalWaiting ??
        queuePerformance.reduce((sum, queue) => sum + (queue.waitingCount ?? 0), 0);
      const avgWaitTime = Number(analyticsOverview.avgWaitTime ?? 0);

      if (totalWaiting > 50) {
        generatedAlerts.push({
          id: 'system-total-waiting',
          type: 'warning',
          message: `В очереди ожидают ${totalWaiting} посетителей, проверьте распределение по окнам`,
          timestamp: now,
          acknowledged: acknowledgedMap.get('system-total-waiting') ?? false,
        });
      }

      if (avgWaitTime > 20) {
        generatedAlerts.push({
          id: 'system-avg-wait',
          type: 'warning',
          message: `Среднее время ожидания выросло до ${avgWaitTime.toFixed(1)} минут`,
          timestamp: now,
          acknowledged: acknowledgedMap.get('system-avg-wait') ?? false,
        });
      }

      queuePerformance.forEach((queue) => {
        const waiting = queue.waitingCount ?? 0;
        const avgService = Number(queue.avgServiceTimeMinutes ?? 0);
        const efficiency = Number(queue.efficiency ?? 0);
        const queueId = queue.id || queue.name;

        if (queue.status !== 'active' && waiting > 0) {
          const id = `queue-paused-${queueId}`;
          generatedAlerts.push({
            id,
            type: 'error',
            message: `Очередь «${queue.name}» имеет статус ${getStatusText(queue.status)} при ${waiting} ожидающих`,
            timestamp: now,
            acknowledged: acknowledgedMap.get(id) ?? false,
          });
        }

        if (waiting > 25 && queue.status === 'active') {
          const id = `queue-waiting-${queueId}`;
          generatedAlerts.push({
            id,
            type: 'warning',
            message: `В очереди «${queue.name}» накопилось ${waiting} посетителей`,
            timestamp: now,
            acknowledged: acknowledgedMap.get(id) ?? false,
          });
        }

        if (efficiency < 50 && (queue.completedToday ?? 0) + waiting >= 20) {
          const id = `queue-efficiency-${queueId}`;
          generatedAlerts.push({
            id,
            type: 'warning',
            message: `Эффективность очереди «${queue.name}» снизилась до ${efficiency}%`,
            timestamp: now,
            acknowledged: acknowledgedMap.get(id) ?? false,
          });
        }

        if (avgService > 25) {
          const id = `queue-service-${queueId}`;
          generatedAlerts.push({
            id,
            type: 'info',
            message: `Среднее время обслуживания в очереди «${queue.name}» достигло ${avgService.toFixed(1)} минут`,
            timestamp: now,
            acknowledged: acknowledgedMap.get(id) ?? false,
          });
        }
      });

      if (totalOperators > 0) {
        const activeOperators = analyticsOverview.operatorsActive ?? 0;
        const activeShare = activeOperators / totalOperators;
        if (activeShare < 0.5) {
          generatedAlerts.push({
            id: 'system-operators-low',
            type: 'error',
            message: `Активно только ${activeOperators} из ${totalOperators} операторов`,
            timestamp: now,
            acknowledged: acknowledgedMap.get('system-operators-low') ?? false,
          });
        } else if (activeShare < 0.75) {
          generatedAlerts.push({
            id: 'system-operators-warning',
            type: 'warning',
            message: `Активно ${activeOperators} из ${totalOperators} операторов, возможно потребуется усиление смены`,
            timestamp: now,
            acknowledged: acknowledgedMap.get('system-operators-warning') ?? false,
          });
        }
      }

      const lastHour = analyticsOverview.lastHour;
      const previousHour = analyticsOverview.previousHour;
      if (lastHour && previousHour && previousHour.created > 0) {
        const createdTrend = (lastHour.created - previousHour.created) / previousHour.created;
        if (createdTrend > 0.4) {
          generatedAlerts.push({
            id: 'system-demand-spike',
            type: 'info',
            message: `Поток посетителей вырос на ${(createdTrend * 100).toFixed(0)}% за последний час`,
            timestamp: now,
            acknowledged: acknowledgedMap.get('system-demand-spike') ?? false,
          });
        }
      }

      return generatedAlerts;
    });
  }, [analyticsOverview, totalOperators]);


  // Обработка управляющих действий
  const handleControlAction = async (action: string) => {
    try {
      let endpoint = '';
      const body: any = { action };

      switch (action) {
        case 'start-all':
          endpoint = '/api/queues/bulk-action';
          body.status = 'active';
          break;
        case 'pause-all':
          endpoint = '/api/queues/bulk-action';
          body.status = 'paused';
          break;
        case 'emergency-stop':
          endpoint = '/api/system/emergency-stop';
          break;
      }

      const response = await apiClient.post(endpoint, body);
      
      if (response.status === 200) {
        refreshAll();
        setControlDialog(null);
        // Добавляем уведомление об успешном действии
        const newAlert: SystemAlert = {
          id: Date.now().toString(),
          type: 'success',
          message: `Действие "${action}" выполнено успешно`,
          timestamp: new Date(),
          acknowledged: false
        };
        setAlerts(prev => [newAlert, ...prev]);
      }
    } catch (error) {
      console.error('Ошибка выполнения действия:', error);
      const errorAlert: SystemAlert = {
        id: Date.now().toString(),
        type: 'error',
        message: `Ошибка выполнения действия "${action}"`,
        timestamp: new Date(),
        acknowledged: false
      };
      setAlerts(prev => [errorAlert, ...prev]);
    }
  };

  // Подтверждение уведомлений
  const acknowledgeAlert = (alertId: string) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId ? { ...alert, acknowledged: true } : alert
    ));
  };

  // Вычисляемые метрики
  const queuePerformance = analyticsOverview?.queuePerformance || [];
  const totalTicketsToday = analyticsOverview?.ticketsTotal || 0;
  const avgWaitTime = analyticsOverview?.avgWaitTime || 0;
  const avgWaitTimePreviousDay = analyticsOverview?.avgWaitTimePreviousDay ?? null;
  const totalWaiting = analyticsOverview?.totalWaiting ?? queuePerformance.reduce((sum: number, queue: any) => sum + (queue.waitingCount || 0), 0);
  const lastHourStats = analyticsOverview?.lastHour;
  const prevHourStats = analyticsOverview?.previousHour;

  const activeOperators =
    analyticsOverview?.operatorsActive ??
    (operatorsData?.data?.operators
      ? operatorsData.data.operators.filter((op: any) => op.status === 'available' || op.status === 'busy').length
      : 0);
  const previousHourActiveOperators = prevHourStats?.activeOperators ?? null;

  const calcPercentChange = (current: number | null, previous: number | null | undefined) => {
    if (current === null || current === undefined || previous === null || previous === undefined || previous === 0) {
      return null;
    }
    const diff = ((current - previous) / previous) * 100;
    return Number(diff.toFixed(1));
  };

  const ticketsChange = calcPercentChange(lastHourStats?.created ?? null, prevHourStats?.created ?? null);
  const waitTimeChange = calcPercentChange(avgWaitTime, avgWaitTimePreviousDay);
  const operatorChange = calcPercentChange(activeOperators, previousHourActiveOperators);

  const metrics: MetricCard[] = [
    {
      title: 'Талонов сегодня',
      value: totalTicketsToday,
      changeText: ticketsChange !== null && ticketsChange !== undefined && ticketsChange !== 0
        ? `${ticketsChange > 0 ? '+' : ''}${ticketsChange}% к предыдущему часу`
        : undefined,
      trend: ticketsChange !== null && ticketsChange !== undefined
        ? (ticketsChange > 0 ? 'up' : ticketsChange < 0 ? 'down' : 'neutral')
        : 'neutral',
      icon: <AssignmentIcon />,
      color: 'success',
    },
    {
      title: 'В очереди сейчас',
      value: totalWaiting,
      trend: 'neutral',
      icon: <QueueIcon />,
      color: 'primary',
    },
    {
      title: 'Активных операторов',
      value: `${activeOperators}/${totalOperators}`,
      changeText: operatorChange !== null && operatorChange !== undefined && operatorChange !== 0
        ? `${operatorChange > 0 ? '+' : ''}${operatorChange}% к предыдущему часу`
        : undefined,
      trend: operatorChange !== null && operatorChange !== undefined
        ? (operatorChange > 0 ? 'up' : operatorChange < 0 ? 'down' : 'neutral')
        : 'neutral',
      icon: <PeopleIcon />,
      color: 'secondary',
    },
    {
      title: 'Среднее ожидание',
      value: `${Number(avgWaitTime).toFixed(1)} мин`,
      changeText: waitTimeChange !== null && waitTimeChange !== undefined && waitTimeChange !== 0
        ? `${waitTimeChange > 0 ? '+' : ''}${waitTimeChange}% к вчера`
        : undefined,
      trend: waitTimeChange !== null && waitTimeChange !== undefined
        ? (waitTimeChange > 0 ? 'up' : waitTimeChange < 0 ? 'down' : 'neutral')
        : 'neutral',
      icon: <AccessTimeIcon />,
      color: 'warning',
    },
  ];

  // Данные для графиков
  const hourlyData =
    analyticsOverview?.ticketsByHour?.length
      ? analyticsOverview.ticketsByHour
          .sort((a: any, b: any) => a.hour - b.hour)
          .map((item: any) => ({
            hour: `${item.hour.toString().padStart(2, '0')}:00`,
            tickets: Number(item.created ?? 0),
            waitTime: Number(item.avgWaitTime ?? 0),
            operators: Number(item.activeOperators ?? 0),
            completed: Number(item.completed ?? 0),
          }))
      : Array.from({ length: 24 }, (_, i) => ({
          hour: `${i.toString().padStart(2, '0')}:00`,
          tickets: 0,
          completed: 0,
          waitTime: 0,
          operators: 0,
        }));

  const queueDistribution = queuePerformance
    .filter((queue: any) => typeof queue.waitingCount === 'number')
    .map((queue: any) => ({
      name: queue.name,
      value: queue.waitingCount || 0
    }));

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'available':
        return 'success';
      case 'busy':
        return 'warning';
      case 'break':
        return 'info';
      case 'paused':
      case 'offline':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    const statusTexts = {
      active: 'Активна',
      paused: 'Приостановлена',
      closed: 'Закрыта',
      available: 'Свободен',
      busy: 'Занят',
      break: 'Перерыв',
      offline: 'Оффлайн',
    };
    return statusTexts[status as keyof typeof statusTexts] || status;
  };

  const getTrendIcon = (trend?: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon sx={{ transform: 'rotate(0deg)' }} />;
      case 'down':
        return <TrendingUpIcon sx={{ transform: 'rotate(180deg)' }} />;
      default:
        return null;
    }
  };

  const unacknowledgedAlertsCount = alerts.filter(alert => !alert.acknowledged).length;

  return (
    <>
      <Helmet>
        <title>Дашборд - ELEKTROM</title>
      </Helmet>

      <Box>
        {/* Заголовок */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Панель управления
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Мониторинг и управление системой очередей в реальном времени
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Обновить данные">
              <IconButton onClick={refreshAll} color="primary" size="large">
            <RefreshIcon />
          </IconButton>
            </Tooltip>
            <Badge badgeContent={unacknowledgedAlertsCount} color="error">
              <IconButton color="primary" size="large">
                <NotificationsIcon />
              </IconButton>
            </Badge>
            <Button variant="outlined" startIcon={<SettingsIcon />}>
              Настройки
            </Button>
          </Box>
        </Box>

        {/* Системные уведомления */}
        {unacknowledgedAlertsCount > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            У вас {unacknowledgedAlertsCount} непрочитанных уведомлений. Проверьте вкладку "Уведомления".
          </Alert>
        )}

        {/* Основные метрики */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {metrics.map((metric, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card elevation={3} sx={{ 
                background: `linear-gradient(135deg, ${
                  metric.color === 'success' ? '#4CAF50' : 
                  metric.color === 'primary' ? '#2196F3' :
                  metric.color === 'warning' ? '#FF9800' :
                  metric.color === 'secondary' ? '#9C27B0' : '#F44336'
                } 0%, ${
                  metric.color === 'success' ? '#66BB6A' : 
                  metric.color === 'primary' ? '#42A5F5' :
                  metric.color === 'warning' ? '#FFB74D' :
                  metric.color === 'secondary' ? '#BA68C8' : '#EF5350'
                } 100%)`,
                color: 'white'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                        {metric.title}
                      </Typography>
                      <Typography variant="h3" component="div" sx={{ fontWeight: 600, mb: 1 }}>
                        {metric.value}
                      </Typography>
                      {metric.changeText && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getTrendIcon(metric.trend)}
                          <Typography variant="body2" sx={{ ml: 0.5, opacity: 0.9 }}>
                            {metric.changeText}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ opacity: 0.8 }}>
                      {metric.icon}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Табы с детальной информацией */}
        <Card elevation={3}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
              <Tab label="Мониторинг" icon={<AssessmentIcon />} />
              <Tab label="Очереди" icon={<QueueIcon />} />
              <Tab label="Операторы" icon={<PeopleIcon />} />
              <Tab label={`Уведомления ${unacknowledgedAlertsCount > 0 ? `(${unacknowledgedAlertsCount})` : ''}`} icon={<NotificationsIcon />} />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
              <Grid item xs={12} lg={8}>
              <Typography variant="h6" gutterBottom>
                  Активность системы по часам
              </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <RechartsTooltip />
                    <Line type="monotone" dataKey="tickets" stroke="#2196F3" strokeWidth={2} name="Талоны" />
                    <Line type="monotone" dataKey="waitTime" stroke="#FF6B6B" strokeWidth={2} name="Время ожидания (мин)" />
                    <Line type="monotone" dataKey="operators" stroke="#4CAF50" strokeWidth={2} name="Активные операторы" />
                  </LineChart>
                </ResponsiveContainer>
              </Grid>
              <Grid item xs={12} lg={4}>
                <Typography variant="h6" gutterBottom>
                  Распределение по очередям
                          </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={queueDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {queueDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
              <Button 
                variant="contained" 
                startIcon={<PlayIcon />} 
                onClick={() => setControlDialog('start-all')}
                color="success"
              >
                Запустить все очереди
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<PauseIcon />} 
                onClick={() => setControlDialog('pause-all')}
                color="warning"
              >
                Приостановить все
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<StopIcon />} 
                onClick={() => setControlDialog('emergency-stop')}
                color="error"
              >
                Экстренная остановка
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Очередь</TableCell>
                    <TableCell align="center">Статус</TableCell>
                    <TableCell align="center">В ожидании</TableCell>
                    <TableCell align="center">Обслужено сегодня</TableCell>
                    <TableCell align="center">Среднее время</TableCell>
                    <TableCell align="center">Операторы</TableCell>
                    <TableCell align="center">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queuePerformance.map((queue: any) => (
                    <TableRow key={queue.id}>
                      <TableCell component="th" scope="row">
                        <Typography variant="subtitle2">{queue.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {queue.code || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getStatusText(queue.status)}
                          color={getStatusColor(queue.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">{queue.waitingCount || 0}</TableCell>
                      <TableCell align="center">{queue.completedToday || 0}</TableCell>
                      <TableCell align="center">{queue.avgServiceTimeMinutes || 0} мин</TableCell>
                      <TableCell align="center">{queue.activeOperators || 0}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary">
                          <SettingsIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Оператор</TableCell>
                    <TableCell align="center">Окно</TableCell>
                    <TableCell align="center">Статус</TableCell>
                    <TableCell align="center">Обслужено сегодня</TableCell>
                    <TableCell align="center">Текущий талон</TableCell>
                    <TableCell align="center">Среднее время</TableCell>
                    <TableCell align="center">Производительность</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {operatorsData?.data?.operators?.map((operator: any) => (
                    <TableRow key={operator.id}>
                      <TableCell component="th" scope="row">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {operator.firstName?.[0]}{operator.lastName?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {operator.firstName} {operator.lastName}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">#{operator.windowNumber || '-'}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getStatusText(operator.status)}
                          color={getStatusColor(operator.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">{operator.ticketsToday || 0}</TableCell>
                      <TableCell align="center">{operator.currentTicket || '-'}</TableCell>
                      <TableCell align="center">{operator.avgServiceTime || 0} мин</TableCell>
                      <TableCell align="center">
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(
                            totalTicketsToday > 0
                              ? ((operator.ticketsToday || 0) / totalTicketsToday) * 100
                              : 0,
                            100
                          )}
                          sx={{ width: 60 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
              <Typography variant="h6" gutterBottom>
              Системные уведомления
              </Typography>
              <List>
              {alerts.map((alert) => (
                <React.Fragment key={alert.id}>
                  <ListItem
                    sx={{ 
                      bgcolor: alert.acknowledged ? 'transparent' : 'action.hover',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <ListItemIcon>
                      {alert.type === 'warning' && <WarningIcon color="warning" />}
                      {alert.type === 'error' && <ErrorIcon color="error" />}
                      {alert.type === 'success' && <CheckCircleIcon color="success" />}
                      {alert.type === 'info' && <NotificationsIcon color="info" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={alert.message}
                      secondary={`${alert.timestamp.toLocaleTimeString()} • ${alert.acknowledged ? 'Прочитано' : 'Новое'}`}
                    />
                    {!alert.acknowledged && (
                      <Button
                      size="small"
                        onClick={() => acknowledgeAlert(alert.id)}
                      >
                        Отметить как прочитанное
                      </Button>
                    )}
                  </ListItem>
                  <Divider />
                </React.Fragment>
                ))}
              </List>
          </TabPanel>
        </Card>

        {/* Диалог подтверждения управляющих действий */}
        <Dialog open={!!controlDialog} onClose={() => setControlDialog(null)}>
          <DialogTitle>Подтверждение действия</DialogTitle>
          <DialogContent>
            <Typography>
              {controlDialog === 'start-all' && 'Запустить все приостановленные очереди? Это действие сделает доступными все очереди для новых талонов.'}
              {controlDialog === 'pause-all' && 'Приостановить все активные очереди? Новые талоны не будут выдаваться, но текущие клиенты продолжат обслуживание.'}
              {controlDialog === 'emergency-stop' && 'ВНИМАНИЕ! Экстренная остановка немедленно прекратит работу всех очередей и может нарушить обслуживание клиентов. Используйте только в критических ситуациях. Продолжить?'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setControlDialog(null)}>
              Отмена
            </Button>
            <Button
              onClick={() => handleControlAction(controlDialog!)}
              color={controlDialog === 'emergency-stop' ? 'error' : 'primary'}
              variant="contained"
            >
              {controlDialog === 'emergency-stop' ? 'Экстренная остановка' : 'Подтвердить'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};